import { SnitcherBody } from './Snitcher';
import { FacebookPixelsBody } from './FacebookTracker';
import { OpenAiPixelBody } from './OpenAiTracker';
import { LinkedInTrackerBody } from './LinkedInTracker';
import { PinterestTrackerBody } from './PinterestTracker';
import type { ClientConfig } from '@archipro-website/config';
import { useRootData } from '@modules/root';
import {
    hasSaleOrSharingOptOut,
    shouldAllowThirdPartySharing,
} from './allow-third-party-sharing';
import { isConfiguredPixelId } from './is-configured-pixel-id';

/**
 * Body pixels, all suppressed by the shared third-party-sharing gate (the HTTP
 * `Sec-GPC` header where the market honours GPC, or an explicit sale/sharing
 * opt-out from either the signed-in member's preference or the opt-out cookie).
 * The Facebook, Pinterest, and LinkedIn entries are the server-rendered
 * `<noscript><img>` halves of those trackers; their `<script>` halves live in
 * `ClientAdvertisingPixels` and re-read the browser-visible signals.
 *
 * Snitcher and the Google tags render nothing at all: they inject their loaders
 * after hydration, so they are inert without JavaScript and have no no-JS half
 * to suppress.
 */
export const BodyPixels = ({
    snitcherTrackingId,
    facebookPixelArchiproId,
    facebookPixelClientId,
    openAiPixelId,
    pinterestTagArchiproId,
    pinterestTagClientId,
    linkedinPartnerId,
}: Pick<
    ClientConfig['pixels'],
    | 'snitcherTrackingId'
    | 'facebookPixelArchiproId'
    | 'facebookPixelClientId'
    | 'openAiPixelId'
    | 'pinterestTagArchiproId'
    | 'pinterestTagClientId'
    | 'linkedinPartnerId'
>) => {
    const { localisation, globalPrivacyControl, saleSharingOptOut, user } =
        useRootData();
    const allowSharing = shouldAllowThirdPartySharing(
        localisation.countryConfig.honourGlobalPrivacyControl,
        globalPrivacyControl,
        hasSaleOrSharingOptOut(user) || saleSharingOptOut,
    );

    const hasFacebookPixel =
        isConfiguredPixelId(facebookPixelArchiproId) ||
        isConfiguredPixelId(facebookPixelClientId);
    const hasOpenAiPixel = isConfiguredPixelId(openAiPixelId);
    const hasPinterestPixel =
        isConfiguredPixelId(pinterestTagArchiproId) ||
        isConfiguredPixelId(pinterestTagClientId);

    return (
        <>
            {allowSharing && snitcherTrackingId && (
                <SnitcherBody id={snitcherTrackingId} />
            )}
            {allowSharing && hasFacebookPixel && (
                <FacebookPixelsBody
                    pixel={facebookPixelArchiproId}
                    pixelClient={facebookPixelClientId}
                />
            )}
            {allowSharing && hasOpenAiPixel && (
                <OpenAiPixelBody pixelId={openAiPixelId} />
            )}
            {allowSharing && hasPinterestPixel && (
                <PinterestTrackerBody
                    pixelArchipro={pinterestTagArchiproId}
                    pixelClient={pinterestTagClientId}
                />
            )}
            {allowSharing && isConfiguredPixelId(linkedinPartnerId) && (
                <LinkedInTrackerBody partnerId={linkedinPartnerId} />
            )}
        </>
    );
};
